import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import SeoHead from "@/components/SeoHead";
import JsonLd, {
  buildBreadcrumbSchema,
  buildItemListSchema,
  buildCollectionPageSchema,
} from "@/components/JsonLd";
import { useBranding } from "@/context/BrandingContext";

const Shop = () => {
  const { category } = useParams();
  const { branding } = useBranding();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("new");
  const [shopSeo, setShopSeo] = useState(null); // global "shop" SEO fallback

  useEffect(() => {
    setLoading(true);
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
    const params = {};
    if (category) params.category = category;
    api.get("/products", { params }).then(r => setProducts(r.data)).finally(() => setLoading(false));
    // Always fetch the global "shop" SEO so we can use it as a fallback when a
    // specific category does not have its own SEO copy filled in.
    api.get("/seo/shop").then(r => setShopSeo(r.data)).catch(() => setShopSeo(null));
  }, [category]);

  const sorted = [...products].sort((a, b) => {
    if (sort === "price_low") return a.price - b.price;
    if (sort === "price_high") return b.price - a.price;
    return 0;
  });

  const activeCat = categories.find(c => c.slug === category);
  const brandName = branding?.brand_name ? branding.brand_name.charAt(0).toUpperCase() + branding.brand_name.slice(1) : "Rieave";

  // SEO resolution order (per category, then global "shop", then sensible defaults)
  const seo = (() => {
    if (activeCat) {
      return {
        title: activeCat.seo_title || `${activeCat.name} — ${brandName}`,
        description: activeCat.seo_description || activeCat.description ||
          `Discover ${activeCat.name.toLowerCase()} crafted to be worn every day.`,
        keywords: activeCat.seo_keywords || `${activeCat.name.toLowerCase()}, jewelry, ${brandName.toLowerCase()}`,
        image: activeCat.og_image || activeCat.image_url || "",
        canonical: typeof window !== "undefined" ? window.location.origin + `/shop/${activeCat.slug}` : "",
      };
    }
    return {
      title: shopSeo?.title || `Shop all jewelry — ${brandName}`,
      description: shopSeo?.description || "Explore the complete rieave collection — necklaces, rings, earrings and bracelets.",
      keywords: shopSeo?.keywords || "jewelry, necklaces, rings, earrings, bracelets",
      image: shopSeo?.og_image || "",
      canonical: typeof window !== "undefined" ? window.location.origin + "/shop" : "",
    };
  })();

  return (
    <div className="py-12 md:py-16" data-testid="shop-page">
      <SeoHead
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        image={seo.image}
        canonical={seo.canonical}
        robots={activeCat?.seo_robots || shopSeo?.seo_robots}
      />
      <JsonLd
        data={[
          buildBreadcrumbSchema([
            { name: "Home", url: typeof window !== "undefined" ? window.location.origin + "/" : "/" },
            { name: "Shop", url: typeof window !== "undefined" ? window.location.origin + "/shop" : "/shop" },
            ...(activeCat ? [{ name: activeCat.name, url: typeof window !== "undefined" ? window.location.origin + `/shop/${activeCat.slug}` : `/shop/${activeCat.slug}` }] : []),
          ]),
          buildCollectionPageSchema({
            name: activeCat ? activeCat.name : "All jewelry",
            description: seo.description,
            url: seo.canonical,
          }),
          buildItemListSchema(sorted.slice(0, 30)),
        ].filter(Boolean)}
      />
      <div className="rieave-container">
        <div className="mb-10 text-center">
          <p className="eyebrow mb-3">{activeCat ? activeCat.name : "All jewelry"}</p>
          <h1 className="font-hero text-5xl md:text-6xl text-charcoal">
            {activeCat ? (
              <>Discover <span className="serif-italic text-rose">{activeCat.name.toLowerCase()}</span></>
            ) : (
              <>Everything <span className="serif-italic text-rose">rieave.</span></>
            )}
          </h1>
        </div>

        <div className="flex gap-2 flex-wrap justify-center mb-10">
          <Link to="/shop" className={`px-5 py-2 rounded-full font-label text-[11px] uppercase tracking-[0.22em] transition ${!category ? "bg-charcoal text-cream" : "bg-white border border-border text-charcoal hover:bg-lavender/60"}`} data-testid="shop-filter-all">All</Link>
          {categories.map(c => (
            <Link
              key={c.slug} to={`/shop/${c.slug}`}
              className={`px-5 py-2 rounded-full font-label text-[11px] uppercase tracking-[0.22em] transition ${category === c.slug ? "bg-charcoal text-cream" : "bg-white border border-border text-charcoal hover:bg-lavender/60"}`}
              data-testid={`shop-filter-${c.slug}`}
            >
              {c.name}
            </Link>
          ))}
        </div>

        <div className="flex justify-between items-center mb-6">
          <p className="font-label text-xs uppercase tracking-[0.22em] text-mauve">{sorted.length} pieces</p>
          <select
            value={sort} onChange={(e) => setSort(e.target.value)}
            className="bg-white border border-border rounded-full px-4 py-2 font-label text-xs uppercase tracking-[0.2em] text-charcoal"
            data-testid="shop-sort"
          >
            <option value="new">Newest</option>
            <option value="price_low">Price: low to high</option>
            <option value="price_high">Price: high to low</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-20 font-cormorant text-xl text-mauve">Loading the collection…</div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-20 font-cormorant text-xl text-mauve">No pieces found here — new arrivals soon.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {sorted.map(p => <ProductCard key={p.id} product={p} badge={p.tags?.includes("new") ? "New" : p.tags?.includes("best_seller") ? "Best seller" : null} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Shop;
